<template>
  <div>
    <main-toolbar>
      <v-toolbar-title>Input</v-toolbar-title>
      <v-spacer />
      <v-btn
        v-if="!readOnly && machineId && inputTypeId"
        text
        @click="editRecordId = 'new'"
        :disabled="loading || dataLoading"
      >
        <v-icon small>fa-plus</v-icon>
        <div v-if="!appSmall">&nbsp;Create</div>
      </v-btn>
      <div slot="extension" style="width: 100%;">
        <v-layout row>
          <v-flex xs6 class="pl-2 pr-2">
            <MachinesPicker light :value="machineId" @input="update('machineId', $event)" />
          </v-flex>
          <v-flex xs6 v-if="machineId">
            <v-select
              :items="inputTypes.map(v => ({text: v.name, value: v.key}))"
              :class="{small: appSmall}"
              class="input-type-selector pl-2 pr-2"
              placeholder="Select Input Type"
              @change="update('inputTypeId', $event)"
              hide-details
              :value="inputTypeId"
              light
            />
          </v-flex>
        </v-layout>
      </div>
    </main-toolbar>
    <v-container
      style="position: absolute; top: 0; left: 0; width: 100%; z-index: 10;"
      class="text-center"
      fluid
      align-center
      justify-center
      v-if="loading"
    >
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      />
    </v-container>
    <v-simple-table
      v-if="machineId && inputTypeId"
      class="elevation-1"
      fill-height
      style="min-width: 100%"
    >
      <thead>
        <tr>
          <th v-if="!readOnly || userRole === 'ROLE_SELECTION'" style="width: 80px;"></th>
          <th
            v-for="(header, $headerIndex) in headers"
            :key="$headerIndex"
          >
            {{ header.text }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, $rowIndex) in records"
          :key="$rowIndex"
        >
          <td class="layout pt-1" v-if="!readOnly || userRole === 'ROLE_SELECTION'">
            <v-btn
              icon
              small
              color="black"
              :disabled="loading"
              @click="editRecordId = item.id"
            >
              <v-icon small>fa-edit</v-icon>
            </v-btn>
            <v-btn
              v-if="!readOnly"
              class="ml-0"
              icon
              small
              color="black"
              :disabled="loading"
              @click="deleteItem(item)"
            >
              <v-icon small>fa-trash-alt</v-icon>
            </v-btn>
          </td>
          <td
            v-for="(header, $index) in headers"
            :key="$index"
          >{{ item[header.value] }}</td>
          <td>
            <v-icon v-if="item.current">fa-check</v-icon>
          </td>
        </tr>
      </tbody>
    </v-simple-table>
    <InputEditor
      v-if="editRecordId"
      :recordId="editRecordId"
      :machineId="machineId"
      :inputTypeId="inputTypeId"
      @close="closeEditor"
    />
  </div>
</template>

<script>
import MachinesPicker from '@/components/MachinesPicker'
import { mapState, mapActions } from 'vuex'
import PaginationMixin from '@/mixins/PaginationMixin'
import inputTypes from './inputTypes'
import InputEditor from './InputEditor'

export default {
  mixins: [PaginationMixin],
  components: {
    InputEditor,
    MachinesPicker
  },
  data () {
    return {
      inputTypes,
      loading: false,
      machineId: null,
      inputTypeId: null,
      editRecordId: null
    }
  },
  computed: {
    dataLoading () {
      return false
    },
    inputType () {
      if (!this.inputTypeId) {
        return null
      }
      return inputTypes.find(t => t.key === this.inputTypeId)
    },
    headers () {
      let headers = []
      if (this.inputType) {
        headers = headers.concat(this.inputType.fields.filter(field => !(field[2] && field[2].hideTable)).map(field => ({
          text: field[1],
          align: 'left',
          value: field[0],
          sortable: false
        })))
        headers.push({
          text: 'Current',
          align: 'left',
          value: 'current',
          sortable: false
        })
      }
      return headers
    },
    meta () {
      if (this.inputTypeId) {
        return this[this.inputTypeId + 'Meta'] || {}
      }
      return {}
    },
    records () {
      if (this.inputTypeId) {
        return this[this.inputTypeId + 'Data'] || []
      }
      return []
    },
    ...mapState('filminfo', {
      filminfoLoading: 'loading',
      filminfoData: 'data',
      filminfoMeta: 'meta'
    }),
    ...mapState('loadrecipe', {
      loadrecipeLoading: 'loading',
      loadrecipeData: 'data',
      loadrecipeMeta: 'meta'
    })
  },
  methods: {
    update (key, value) {
      this.$set(this, key, value)
      if (this.machineId && this.inputTypeId) {
        this.loadData()
      }
    },
    closeEditor () {
      this.editRecordId = null
      this.loadData()
    },
    ...mapActions('filminfo', {
      filminfoFind: 'find',
      filminfoDelete: 'delete'
    }),
    ...mapActions('loadrecipe', {
      loadrecipeFind: 'find',
      loadrecipeDelete: 'delete'
    }),
    async loadData () {
      this.loading = true
      try {
        this.$router.setQueryParams({ machineId: this.machineId || undefined, inputTypeId: this.inputTypeId || undefined })
        await this[this.inputTypeId + 'Find']({
          urlParams: {
            machineId: this.machineId
          },
          params: {
            rowCount: 500
          }
        })
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    },
    async deleteItem (item) {
      if (!window.confirm('Are you sure you want to delete this item?')) {
        return
      }
      this.loading = true
      try {
        await this[this.inputTypeId + 'Delete']([
          item.id,
          { urlParams: {
            machineId: this.machineId
          } }
        ])
        this.loading = false
      } catch (e) {
        this.loading = false
        throw e
      }
    }
  },
  async created () {
    if (this.$route.query.machineId && this.$route.query.machineId !== 'undefined') {
      this.machineId = this.$route.query.machineId + ''
    }
    if (this.$route.query.inputTypeId && this.$route.query.inputTypeId !== 'undefined') {
      this.inputTypeId = this.$route.query.inputTypeId
    }
    if (this.machineId && this.inputTypeId) {
      await this.loadData()
    }
  }
}
</script>
